// Dominoes in VR — fully usable with bare hands (poke or pinch-ray): the join
// panel over the table, then a lap panel at your chair (grab bar to move it)
// with the lobby + target picker, the open ends as big buttons, your tiles as
// big tappable tiles, Pass / Draw, the score, rules and leaderboard.
// Table-local, floor origin.

import React, { useEffect, useState } from 'react';
import { Text } from '@react-three/drei';
import { tableAction, handOf } from '../gameStore';
import { leaveTable } from '../kit/LobbyKit';
import { VRBtn, VRJoinPanel, VRLobbyBody, VRRulesPanel, VRLeaderboardPanel, SeatPanelMount, vrColors as colors, noRaycast, Surface } from '../kit/vrKit';
import { legalPlays, ends, sideOf, SEAT_NAMES, TARGETS, DEFAULT_TARGET, type DominoesState, type End } from '../../../../supabase/functions/games/dominoes.ts';
import { dominoFaceTexture } from './DominoTile';
import { handSummary } from './DominoesOverlay';
import type { VRPanelProps } from '../types';

const TW = 0.056, TH = 0.1, STEP = 0.074, PER_ROW = 9;

function VRTile({ tile, x, y, playable, picked, onPress }: { tile: string; x: number; y: number; playable: boolean; picked: boolean; onPress: () => void }) {
  const [hover, setHover] = useState(false);
  const lift = (hover && playable) || picked ? 0.008 : 0;
  return (
    <group position={[x, y + lift, 0.006]} name={`vr-domino-${tile}`}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); if (playable) onPress(); }}>
      {/* Generous hit area around the tile for fingertips. */}
      <Surface width={TW + 0.012} height={TH + 0.012} color={picked ? colors.teal : hover && playable ? colors.hover : colors.panel} r={0.008} />
      <mesh position={[0, 0, 0.002]} raycast={noRaycast as any}>
        <planeGeometry args={[TW, TH]} />
        <meshBasicMaterial map={dominoFaceTexture(tile)} color={playable || picked ? '#ffffff' : '#7d8288'} toneMapped={false} />
      </mesh>
    </group>
  );
}

export function DominoesVRPanel({ def, pid, worldId, entry, table, seat }: VRPanelProps) {
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const st = table.state;
  const g = st.g as DominoesState | null;
  const lastKey = g ? `${g.handNo}:${g.line.length}:${g.turn}` : '';
  useEffect(() => { setPicked(null); }, [lastKey]);

  if (seat < 0) return <VRJoinPanel def={def} pid={pid} entry={entry} worldId={worldId} />;

  const hand = handOf(entry);
  const W = 0.8;
  const myTurn = !!g && st.phase === 'playing' && g.phase === 'playing' && g.turn === seat;
  const legal = g && myTurn ? legalPlays(g, hand) : [];
  const fits = (t: string) => legal.filter((p) => p.tile === t).map((p) => p.end);
  const play = (tile: string, end: End) => { setPicked(null); void tableAction(pid, 'move', { move: { type: 'play', tile, end } }); };
  const tap = (t: string) => {
    const f = fits(t);
    if (!f.length || entry.busy) return;
    if (f.length === 1) play(t, f[0]); else setPicked((p) => (p === t ? null : t));
  };
  const showHand = !!g && st.phase === 'playing' && g.phase === 'playing' && hand.length > 0;
  const rows = showHand ? Math.ceil(hand.length / PER_ROW) : 0;

  let body: React.ReactNode = null;
  let H = 0.36;
  if (st.phase === 'lobby') {
    const cur = Number(st.options?.target) || DEFAULT_TARGET;
    H = 0.42;
    body = <group position={[0, 0.05, 0]}>
      <VRLobbyBody def={def} pid={pid} entry={entry} />
      <Text raycast={noRaycast} position={[-0.3, -0.07, 0.004]} fontSize={0.017} color={colors.muted}>Play to</Text>
      {TARGETS.map((t, i) => (
        <VRBtn key={t} name={`vr-dm-target-${t}`} label={String(t)} x={-0.16 + i * 0.15} y={-0.07} w={0.13} h={0.055}
          active={cur === t} disabled={entry.busy} onPress={() => void tableAction(pid, 'configure', { options: { target: t } })} />
      ))}
      <Text raycast={noRaycast} position={[0, -0.135, 0.004]} fontSize={0.014} color={colors.muted}>4 players: partners · 2 players: head-to-head</Text>
    </group>;
  } else if (st.phase === 'over' && g) {
    const won = g.winner === sideOf(g, seat);
    body = <group position={[0, 0.03, 0]}>
      <Text raycast={noRaycast} position={[0, 0.08, 0.004]} fontSize={0.024} color={colors.teal}>{`${won ? 'You win!' : 'Game over'}  ${g.scores.join(' – ')}`}</Text>
      <VRBtn label="Rematch" x={-0.19} y={0} w={0.34} h={0.06} active disabled={entry.busy} onPress={() => void tableAction(pid, 'rematch')} />
      <VRBtn name="vr-game-leaders-over" label="Leaderboard" x={0.19} y={0} w={0.34} h={0.06} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
    </group>;
  } else if (g) {
    const e = ends(g);
    const stuck = myTurn && legal.length === 0;
    const pickedEnds = picked ? fits(picked) : [];
    const turnName = st.seats[g.turn]?.name ?? SEAT_NAMES[g.turn];
    const status = g.phase === 'hand_over' ? handSummary(g, st, seat)
      : myTurn ? (g.line.length === 0 ? (g.mustLead ? `Your lead — set the ${g.mustLead}` : 'Your lead — any tile')
        : stuck ? (g.boneyard > 0 ? 'No play — draw' : 'No play — pass') : picked ? 'Fits both — tap an end' : 'Your turn — tap a tile')
      : `${turnName}'s turn`;
    H = 0.3 + rows * (TH + 0.02);
    const top = H / 2;
    body = <>
      <Text raycast={noRaycast} position={[0, top - 0.085, 0.004]} fontSize={0.019} maxWidth={W - 0.06} textAlign="center" color={myTurn ? colors.teal : colors.text}>{status}</Text>
      {/* Open ends: big targets when a tile fits both. */}
      <VRBtn name="vr-dm-left" label={e ? `◀ Left end  ${e[0]}` : 'Left end'} x={-0.2} y={top - 0.15} w={0.3} h={0.06} fontSize={0.017}
        active={pickedEnds.includes('left')} disabled={!pickedEnds.includes('left') || entry.busy} onPress={() => picked && play(picked, 'left')} />
      <VRBtn name="vr-dm-right" label={e ? `Right end  ${e[1]} ▶` : 'Right end'} x={0.2} y={top - 0.15} w={0.3} h={0.06} fontSize={0.017}
        active={pickedEnds.includes('right')} disabled={!pickedEnds.includes('right') || entry.busy} onPress={() => picked && play(picked, 'right')} />
      {stuck && (
        <VRBtn name="vr-dm-passdraw" label={g.boneyard > 0 ? 'Draw' : 'Pass'} x={0} y={-top + 0.06 + rows * (TH + 0.02)} w={0.3} h={0.06} active disabled={entry.busy}
          onPress={() => void tableAction(pid, 'move', { move: { type: g.boneyard > 0 ? 'draw' : 'pass' } })} />
      )}
      {showHand && hand.map((t, k) => {
        const r = Math.floor(k / PER_ROW), inRow = Math.min(PER_ROW, hand.length - r * PER_ROW), c = k % PER_ROW;
        return <VRTile key={t} tile={t} x={-((inRow - 1) * STEP) / 2 + c * STEP} y={-top + 0.03 + TH / 2 + (rows - 1 - r) * (TH + 0.02)}
          playable={fits(t).length > 0 && !entry.busy} picked={picked === t} onPress={() => tap(t)} />;
      })}
    </>;
  }

  const scoreText = g && st.phase !== 'lobby'
    ? g.mode === 'partners' ? `Us ${g.scores[sideOf(g, seat)]}  ·  Them ${g.scores[1 - sideOf(g, seat)]}  (to ${g.target})`
      : `${g.sides.map((s, i) => `${s[0] === seat ? 'You' : (st.seats[s[0]]?.name ?? SEAT_NAMES[s[0]]).slice(0, 10)} ${g.scores[i]}`).join(' · ')}${g.boneyard ? ` · yard ${g.boneyard}` : ''}`
    : '';

  return (
    <SeatPanelMount chair={def.chairs[seat]} height={H}>
      <Surface width={W} height={H} color={colors.panel} />
      {body}
      <VRBtn label="Leave" x={W / 2 - 0.07} y={H / 2 - 0.035} w={0.11} h={0.045} fontSize={0.014} onPress={() => leaveTable(pid)} />
      <VRBtn name="vr-game-howto" label="?" x={W / 2 - 0.16} y={H / 2 - 0.035} w={0.05} h={0.045} fontSize={0.016} active={rules} onPress={() => { setBoard(false); setRules((v) => !v); }} />
      <VRBtn name="vr-game-leaders" label="Leaders" x={W / 2 - 0.26} y={H / 2 - 0.035} w={0.12} h={0.045} fontSize={0.014} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
      {scoreText && <Text raycast={noRaycast} position={[-W / 2 + 0.02, H / 2 - 0.035, 0.004]} anchorX="left" fontSize={0.014} maxWidth={0.44} color={colors.muted}>{scoreText}</Text>}
      {rules && <group position={[0, 0, 0.02]}><VRRulesPanel def={def} y={-0.31} onClose={() => setRules(false)} /></group>}
      {board && <group position={[0, 0, 0.02]}><VRLeaderboardPanel def={def} worldId={worldId} refreshKey={st.gameId} onClose={() => setBoard(false)} /></group>}
      {entry.error && <Text raycast={noRaycast} position={[0, -H / 2 + 0.012, 0.004]} fontSize={0.013} color="#ff9b9b">{entry.error}</Text>}
    </SeatPanelMount>
  );
}

export default DominoesVRPanel;
